import { Box } from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/react';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { type FormikProps, type FormikValues, useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { selectCurrUser } from '@/store/bootstrap/selectors';
import { getRequisitionErrorMessage } from '@/components/shared/requisitions/utils/requisitionErrorHandler';

import { AssociateReqModal } from '@/components/shared/requisitions/associate-req/AssociateReqModal';
import { type Requisition } from '@/components/shared/requisitions/associate-req/associateReq.interface';
import { useRequisitionsOptions } from '@/components/shared/requisitions/associate-req/hooks/useRequisitionsOptions';
import { RequisitionsDisplay } from '@/components/shared/requisitions/associate-req/components/RequisitionsDisplay';
import {
  useAddRequisitionsToJob,
  useRemoveRequisitionsFromJob,
} from '@/components/shared/requisitions/associate-req/api/associateReqApi';
import { useShowToast } from '@/hooks';

interface AssociateReqProps {
  requisitions?: Requisition[];
  multiSelect?: boolean;
  userHasReqEnabled?: boolean;
  shouldUseLegacyFlow?: boolean;
  setShowLegacyFlowDynamically?: (_show) => void;
  selectedRequisitions?: Requisition[];
  setSelectedRequisitions?: (_requisitions: Requisition[]) => void;
  setRequisitionForDefaults?: (_requisition: Requisition | null) => void;
  refetchJobData?: () => void;
  buttonText?: string;
  formik?: FormikProps<FormikValues>;
  buttonProps?: unknown;
  offerId?: string;
  offerReq?: Requisition[];
}

const AssociateReq = ({
  requisitions = [],
  multiSelect = false,
  userHasReqEnabled = false,
  shouldUseLegacyFlow = false,
  setShowLegacyFlowDynamically,
  selectedRequisitions = [],
  setSelectedRequisitions = () => {},
  setRequisitionForDefaults,
  refetchJobData,
  buttonText = 'Associate requisitions',
  formik,
  buttonProps,
  offerId,
  offerReq = [],
}: AssociateReqProps) => {
  const associateReqsModal = useDisclosure();
  const [isSelectFieldMode, setIsSelectFieldMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTableUpdating, setIsTableUpdating] = useState(false);
  // Store locally associated requisitions to display even if not in availableRequisitions
  const [locallyAssociatedRequisitions, setLocallyAssociatedRequisitions] =
    useState<Requisition[]>([]);

  const { jobId } = useParams<{ jobId: string }>();
  const currUser = useSelector(selectCurrUser);
  const formikContext = useFormikContext();
  const formikInstance = formik || formikContext;
  const { setFieldValue, values, submitCount } = formikInstance || {};
  const toast = useShowToast();

  const { mutateAsync: addRequisitionsToJob } = useAddRequisitionsToJob();
  const { mutateAsync: removeRequisitionsFromJob } =
    useRemoveRequisitionsFromJob();

  const { requisition } = currUser;
  const { mandatory_for_job_creation = false } = requisition || {};
  const hasSelectedRequisitions = selectedRequisitions.length > 0;

  const {
    availableRequisitions,
    isLoadingRequisitions,
    requisitionsOptions,
    refetchRequisitions,
  } = useRequisitionsOptions({
    multiSelect,
    fallbackRequisitions: requisitions,
    selectedRequisitions: selectedRequisitions,
    offerId,
    offerReq,
  });

  const hasRequisitionValue =
    values?.requisitions_codes || values?.req_id || values?.associate_req_id;

  // Sync requisitions_codes from selectedRequisitions when form has empty value
  useEffect(() => {
    if (!setFieldValue || !selectedRequisitions?.length) return;

    const formCodes = values?.requisitions_codes;
    const hasFormCodes = Array.isArray(formCodes) && formCodes.length > 0;
    const selectedCodes = selectedRequisitions.map((r) => r.code);

    if (!hasFormCodes && selectedCodes.length > 0) {
      setFieldValue(
        'requisitions_codes',
        multiSelect ? selectedCodes : selectedCodes[0]
      );
    }
  }, [
    selectedRequisitions,
    values?.requisitions_codes,
    multiSelect,
    setFieldValue,
  ]);

  const isFieldRequired = multiSelect ? mandatory_for_job_creation : true;
  const showValidationError =
    !hasRequisitionValue && submitCount > 0 && isFieldRequired;

  const selectedRequisitionsOptionsFromForm = useMemo(() => {
    const formValue =
      values?.requisitions_codes || values?.req_id || values?.associate_req_id;
    if (!formValue) return null;
    const codes = Array.isArray(formValue) ? formValue : [formValue];
    // Combine availableRequisitions with locallyAssociatedRequisitions
    // to ensure we can display requisitions that were just associated
    const allRequisitions = [
      ...availableRequisitions,
      ...locallyAssociatedRequisitions.filter(
        (localReq) =>
          !availableRequisitions.some((availReq) => availReq.id === localReq.id)
      ),
    ];

    const selectedReqs = allRequisitions.filter(
      (req) =>
        codes.includes(req.code) &&
        (multiSelect ||
          !req.requisition_association ||
          String(req.requisition_association?.offer?.id) === String(offerId))
    );

    if (selectedReqs.length === 0) return null;

    const options = selectedReqs.map((req) => ({
      value: req.id,
      label: `${req.code} - ${req.role_name}`,
      code: req.code,
      status: req.status,
      department: req.ats_department?.name,
      locations: req.ats_offices?.map((office) => office.name) || [],
    }));

    return multiSelect ? options : options[0] || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values,
    availableRequisitions,
    locallyAssociatedRequisitions,
    multiSelect,
  ]);

  // handle select change for requisitions
  const handleSelectChange = useCallback(
    (selectedOptions) => {
      if (!selectedOptions) {
        setFieldValue('requisitions_codes', null);
        setSelectedRequisitions([]);
        setRequisitionForDefaults?.(null);
        return;
      }

      const selectedIds = Array.isArray(selectedOptions)
        ? selectedOptions.map((opt) => opt.value)
        : [selectedOptions.value];

      const selectedReqs = availableRequisitions.filter((req) =>
        selectedIds.includes(req.id)
      );

      const selectedRequisitionCodes = selectedReqs.map((req) => req.code);

      setFieldValue('requisitions_codes', selectedRequisitionCodes);
      if (!multiSelect) {
        setSelectedRequisitions(selectedReqs);
      }

      // Set first requisition for defaults
      if (selectedReqs.length > 0) {
        setRequisitionForDefaults?.(selectedReqs[0]);
      } else {
        setRequisitionForDefaults?.(null);
      }
    },
    [
      availableRequisitions,
      setFieldValue,
      setSelectedRequisitions,
      multiSelect,
      setRequisitionForDefaults,
    ]
  );

  // delete requisitions for job associated
  const handleDeleteRequisition = useCallback(
    async (requisitionToDelete: Requisition) => {
      if (jobId) {
        setIsDeleting(true);
        try {
          const result = await removeRequisitionsFromJob({
            jobId,
            requisitionCodes: [requisitionToDelete.code],
          });
          const { requisitions } = result?.data?.basic_info || {};
          const updatedRequisitions =
            requisitions?.filter((req) => req.role_name) || [];

          if (result?.data?.id) {
            setSelectedRequisitions(updatedRequisitions);

            const updatedCodes = updatedRequisitions.map((req) => req.code);
            setFieldValue?.(
              'requisitions_codes',
              updatedCodes.length > 0 ? updatedCodes : ''
            );

            if (updatedRequisitions.length > 0) {
              setRequisitionForDefaults?.(updatedRequisitions[0]);
            } else {
              setRequisitionForDefaults?.(null);
            }

            if (!userHasReqEnabled && updatedRequisitions.length === 0) {
              setFieldValue?.('requisitions_codes', '');
              setShowLegacyFlowDynamically?.(true);
            }
            refetchJobData?.();
            // Refetch requisitions list to update available options
            if (updatedRequisitions.length === 0) {
              refetchRequisitions();
            }
            toast({
              title: 'Requisition removed successfully',
              status: 'success',
            });
          } else {
            const errorMessage = getRequisitionErrorMessage(result, false);
            toast({
              title: errorMessage || 'Failed to remove requisition',
              status: 'error',
            });
          }
        } catch (error) {
          const errorMessage = getRequisitionErrorMessage(error, false);
          toast({
            title: errorMessage || 'Failed to remove requisition',
            status: 'error',
          });
        } finally {
          setIsDeleting(false);
        }
      }
    },
    [
      jobId,
      removeRequisitionsFromJob,
      setSelectedRequisitions,
      userHasReqEnabled,
      toast,
      setFieldValue,
      setShowLegacyFlowDynamically,
      refetchJobData,
      refetchRequisitions,
      setRequisitionForDefaults,
    ]
  );

  //default associate requisitions to job
  const handleAssociateRequisitions = useCallback(
    async (newRequisitions: Requisition[]) => {
      // Show loader to prevent showing incremental updates
      setIsTableUpdating(true);

      if (multiSelect && jobId) {
        try {
          const requisitionCodes = newRequisitions.map((req) => req.code);
          await addRequisitionsToJob({
            jobId,
            requisitionCodes,
          });

          // Combine existing with new requisitions, avoiding duplicates
          const existingIds = new Set(
            selectedRequisitions.map((req) => req.id)
          );
          const uniqueNewRequisitions = newRequisitions.filter(
            (req) => !existingIds.has(req.id)
          );
          const combinedRequisitions = [
            ...selectedRequisitions,
            ...uniqueNewRequisitions,
          ];

          // Update all state at once to prevent incremental renders
          setSelectedRequisitions(combinedRequisitions);
          const allCodes = combinedRequisitions.map((req) => req.code);
          setFieldValue('requisitions_codes', allCodes);

          // Point the prefill at the new requisition, like the select paths
          // do — otherwise the defaults stay pinned to whatever was picked at
          // page load and this modal never prefills salary/equity.
          if (uniqueNewRequisitions.length > 0) {
            setRequisitionForDefaults?.(uniqueNewRequisitions[0]);
          }

          // Refetch in background after state is updated
          setTimeout(() => {
            refetchJobData?.();
            refetchRequisitions();
            setIsTableUpdating(false);
          }, 150);
        } catch (error) {
          setIsTableUpdating(false);
          const errorMessage = getRequisitionErrorMessage(error, false);
          toast({
            title: errorMessage || 'Failed to add requisitions',
            status: 'error',
          });
          return;
        }
      } else {
        // For non-multiSelect, just update directly
        setSelectedRequisitions(newRequisitions);
        const requisitionCodes = newRequisitions.map((req) => req.code);
        setFieldValue('requisitions_codes', requisitionCodes);
        setRequisitionForDefaults?.(newRequisitions[0] ?? null);
        setIsTableUpdating(false);
      }
    },
    [
      multiSelect,
      jobId,
      addRequisitionsToJob,
      selectedRequisitions,
      setSelectedRequisitions,
      setFieldValue,
      toast,
      refetchJobData,
      refetchRequisitions,
      setRequisitionForDefaults,
    ]
  );

  // Handler for "See all requisitions" button - updates form field directly
  const handleAssociateRequisitionsToForm = useCallback(
    (newRequisitions: Requisition[]) => {
      // Store the requisitions locally so they can be displayed even if not in availableRequisitions
      setLocallyAssociatedRequisitions(newRequisitions);

      if (multiSelect) {
        const newCodes = newRequisitions.map((req) => req.code);
        setFieldValue('requisitions_codes', newCodes);
      } else {
        const codes = newRequisitions.map((req) => req.code);
        const codeValue = codes[0] || null;
        setFieldValue('requisitions_codes', codeValue);
        setSelectedRequisitions(newRequisitions);
      }

      // Set first requisition for defaults
      if (newRequisitions.length > 0) {
        setRequisitionForDefaults?.(newRequisitions[0]);
      } else {
        setRequisitionForDefaults?.(null);
      }
    },
    [
      multiSelect,
      setFieldValue,
      setSelectedRequisitions,
      setRequisitionForDefaults,
    ]
  );

  const handleModalClose = useCallback(() => {
    setIsSelectFieldMode(false);
    associateReqsModal.onClose();
  }, [associateReqsModal]);

  // Open modal for updating existing selected requisitions
  const handleOpenModalForButton = useCallback(() => {
    setIsSelectFieldMode(false);
    associateReqsModal.onOpen();
  }, [associateReqsModal]);

  // Open modal from "See all requisitions" button (form direct mode)
  const handleOpenModalForSelectField = useCallback(() => {
    setIsSelectFieldMode(true);
    associateReqsModal.onOpen();
  }, [associateReqsModal]);

  const modalSelectedList = useMemo(() => {
    if (!isSelectFieldMode) {
      return selectedRequisitions;
    }

    const formCodes =
      values?.requisitions_codes ||
      values?.req_id ||
      values?.associate_req_id ||
      [];
    const codes = Array.isArray(formCodes) ? formCodes : [formCodes];

    // Combine availableRequisitions with locallyAssociatedRequisitions
    const allRequisitions = [
      ...availableRequisitions,
      ...locallyAssociatedRequisitions.filter(
        (localReq) =>
          !availableRequisitions.some((availReq) => availReq.id === localReq.id)
      ),
    ];

    return allRequisitions.filter((req) => codes.includes(req.code));
  }, [
    isSelectFieldMode,
    selectedRequisitions,
    values,
    availableRequisitions,
    locallyAssociatedRequisitions,
  ]);

  const shouldRenderComponent =
    !shouldUseLegacyFlow ||
    hasSelectedRequisitions ||
    (shouldUseLegacyFlow && userHasReqEnabled);

  if (!shouldRenderComponent) return null;

  return (
    <Box>
      <RequisitionsDisplay
        multiSelect={multiSelect}
        isFieldRequired={isFieldRequired}
        userHasReqEnabled={userHasReqEnabled}
        shouldUseLegacyFlow={shouldUseLegacyFlow}
        hasSelectedRequisitions={hasSelectedRequisitions}
        selectedRequisitions={selectedRequisitions}
        requisitionsOptions={requisitionsOptions}
        selectedRequisitionsOptionsFromForm={
          selectedRequisitionsOptionsFromForm
        }
        isLoadingRequisitions={isLoadingRequisitions}
        showValidationError={showValidationError}
        buttonText={buttonText}
        onOpenModalForButton={handleOpenModalForButton}
        onOpenModalForSelectField={handleOpenModalForSelectField}
        onSelectChange={handleSelectChange}
        onDeleteRequisition={handleDeleteRequisition}
        isDeleting={isDeleting}
        isTableUpdating={isTableUpdating}
        buttonProps={buttonProps}
        offerReq={offerReq}
      />

      <AssociateReqModal
        key={`associate-req-modal-${
          associateReqsModal.isOpen ? 'open' : 'closed'
        }-${modalSelectedList.map((r) => r.id).join(',')}`}
        isOpen={associateReqsModal.isOpen}
        onClose={handleModalClose}
        onAssociate={
          isSelectFieldMode
            ? handleAssociateRequisitionsToForm
            : handleAssociateRequisitions
        }
        multiSelect={multiSelect}
        selectedList={isSelectFieldMode ? modalSelectedList : []}
        offerId={offerId}
      />
    </Box>
  );
};

export default AssociateReq;
